import { FURNITURE_FAQS, SALES_FAQS, WEBSITE_FAQS, AGENT_FAQS, PHONE_FAQS, RECEIPT_FAQS, REVIEW_FAQS, SEARCH_FAQS, WEBSITES_FAQS } from './faqs';
// What a search engine or an AI assistant reads about each marketing page:
// title, description, canonical address, and schema.org data. Used by the
// prerender step to write the head into each page's HTML, and by the pages
// themselves to set document.title in the browser. One entry per route in
// src/site/marketingRoutes.jsx. Keep every route here, or it ships with the
// home page's head.
export const ORIGIN = 'https://exitiumlabs.com';

const ORG = {
  '@type': 'Organization',
  '@id': `${ORIGIN}/#organization`,
  name: 'Exitium Labs',
  url: `${ORIGIN}/`,
  logo: `${ORIGIN}/e.png`,
  email: 'sales@exitiumlabs.com',
  telephone: '+1-415-624-6941',
};

const faqLd = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
});

const serviceLd = ({ name, description, path, serviceType = 'AI software for furniture retail', audience = 'Furniture retailers' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${ORIGIN}${path}#service`,
  name,
  description,
  url: `${ORIGIN}${path}`,
  provider: { '@id': ORG['@id'] },
  serviceType,
  areaServed: { '@type': 'Country', name: 'US' },
  audience: { '@type': 'BusinessAudience', name: audience },
});

const page = ({ path, title, description, faqs = [], service = true, serviceType, audience, extraLd = [] }) => ({
  path,
  title,
  description,
  canonical: `${ORIGIN}${path === '/' ? '/' : path}`,
  jsonLd: [
    { '@context': 'https://schema.org', ...ORG },
    ...(service && path !== '/' ? [serviceLd({ name: title.split(' | ')[0], description, path, serviceType, audience })] : []),
    ...(faqs.length ? [faqLd(faqs)] : []),
    ...extraLd,
  ],
});

// Filled in as the service pages are written. Order here is the order in
// the sitemap.
export const HEADS = [
  page({
    path: '/',
    title: 'Exitium Labs | The Virtual Designer for Furniture Stores',
    description: 'Exitium is the virtual designer for furniture stores. The customer says what they need, it finds the exact piece from every brand you carry, and sells it on your floor and on your website. The list goes home on their phone. The phone agent, the receipt by text, deliveries and reviews come with it. One showroom, a group of stores, or the brand that supplies them. We also build websites for restaurants, clinics and local services.',
    faqs: FURNITURE_FAQS.slice(0, 6),
  }),
  page({
    path: '/sales-assistant',
    title: 'In-store AI Sales Assistant for Furniture Stores | Exitium Labs',
    description: 'Your salesperson enters every variable a customer gives, the assistant searches a catalog of many brands, and the results go live to the customer’s own portal. The customer leaves with it on their phone.',
    faqs: SALES_FAQS,
  }),
  page({
    path: '/website',
    title: 'AI Sales Platform for Furniture Stores | Exitium Labs',
    description: 'A furniture store\u2019s whole catalog online as an ecommerce showroom with an AI designer that sells by voice or text: it filters, compares, saves and builds the cart. Live on eastbay.direct.',
    faqs: WEBSITE_FAQS,
  }),
  page({
    path: '/website-agent',
    title: 'AI Designer for Furniture Ecommerce Sites | Exitium Labs',
    description: 'An AI designer that sits on top of the furniture ecommerce site you already run. Knows every product, sells by voice or text, acts on the page, and knows when to hand off to a person or capture the lead.',
    faqs: AGENT_FAQS,
  }),
  page({
    path: '/phone-agent',
    title: 'AI Phone Agent for Furniture Stores | Exitium Labs',
    description: 'Keep your number. An AI answers every call knowing your floor and your orders, recognizes returning customers, texts photos mid-call, and transfers to staff on shift. English, Spanish, Arabic.',
    faqs: PHONE_FAQS,
  }),
  page({
    path: '/digital-receipt',
    title: 'Digital Receipts by Text for Furniture Stores | Exitium Labs',
    description: 'A living receipt by text instead of a paper slip or email. Connected to your register, sent on its own at the sale, a live status on every line, and an AI that answers where-is-my-order questions in the thread, any hour. Works with the receipt book, Furniture Wizard, or STORIS.',
    faqs: RECEIPT_FAQS,
  }),
  page({
    path: '/reviews',
    title: 'Review Automation for Furniture Stores | Exitium Labs',
    description: 'A short survey goes out in the receipt thread the day the last piece is delivered. Feedback reaches your team right away and the customer is invited to review you on Google.',
    faqs: REVIEW_FAQS,
  }),
  page({
    path: '/ai-search',
    title: 'AI Search and SEO for Furniture Store Websites | Exitium Labs',
    description: 'Shoppers ask AI where to buy furniture. We build your website so AI can read your whole catalog and recommend your store, and so the shopper who arrives is ready to buy. Measured before and after.',
    faqs: SEARCH_FAQS,
  }),
  page({
    path: '/websites',
    title: 'Websites for Restaurants, Clinics, and Local Businesses | Exitium Labs',
    description: 'We build websites for restaurants, food trucks, clinics, service offices, and furniture stores, each one around the thing that makes the business money: the order, the booking, the call. Drizzled in Stockton saw 75% more online orders after its new site.',
    faqs: WEBSITES_FAQS,
    serviceType: 'Website design for local businesses',
    audience: 'Restaurants, clinics, service offices, and furniture stores',
  }),
];

export const headFor = (path) => HEADS.find((h) => h.path === path) || HEADS[0];
